<app-loading *ngIf="contentReady"></app-loading>

<section class="bb-page">
  <div class="bb-page-shell">

    <!-- Header -->
    <header class="bb-page-header">
      <div class="bb-page-title-block">
        <span class="bb-eyebrow">{{ 'Manage Notifications' | transloco }}</span>
        <h2 class="bb-page-title">{{ 'Manage Notifications' | transloco }}</h2>
      </div>
    </header>

    <!-- Notification Form -->
    <form [formGroup]="notificationForm" (ngSubmit)="addNotification()" enctype="multipart/form-data">
      <div class="bb-card bb-p-20">
        <div class="bb-form-grid">

          @if(submitted && notificationForm.invalid) {
            <div class="bb-form-alert bb-field-full" role="alert">
              {{ 'Review the notification data before saving' | transloco }}

              @if(getMissingRequiredCount() > 0) {
                <span>
                  ({{ getMissingRequiredCount() }} {{ 'required fields' | transloco }})
                </span>
              }
            </div>
          }

          <!-- Arabic Title -->
          <div class="bb-field">
            <label class="bb-label" for="notification_head_ar">
              {{ 'Notification Title (Arabic)' | transloco }}
            </label>

            <input
              class="bb-input"
              id="notification_head_ar"
              type="text"
              formControlName="notification_head_ar"
              placeholder="{{ 'Title in Arabic' | transloco }}"
              [ngClass]="{
                'is-invalid': notificationForm.get('notification_head_ar')?.touched && notificationForm.get('notification_head_ar')?.invalid,
                'is-valid': notificationForm.get('notification_head_ar')?.touched && notificationForm.get('notification_head_ar')?.valid
              }"
            />

            @if(shouldShowError('notification_head_ar')) {
              <div class="bb-field-error">
                {{ getErrorMessage('notification_head_ar', ('Notification Title (Arabic)' | transloco)) }}
              </div>
            }
          </div>

          <!-- English Title -->
          <div class="bb-field">
            <label class="bb-label" for="notification_head_en">
              {{ 'Notification Title (English)' | transloco }}
            </label>

            <input
              class="bb-input"
              id="notification_head_en"
              type="text"
              formControlName="notification_head_en"
              placeholder="{{ 'Title in English' | transloco }}"
              [ngClass]="{
                'is-invalid': notificationForm.get('notification_head_en')?.touched && notificationForm.get('notification_head_en')?.invalid,
                'is-valid': notificationForm.get('notification_head_en')?.touched && notificationForm.get('notification_head_en')?.valid
              }"
            />

            @if(shouldShowError('notification_head_en')) {
              <div class="bb-field-error">
                {{ getErrorMessage('notification_head_en', ('Notification Title (English)' | transloco)) }}
              </div>
            }
          </div>

          <!-- Arabic Body -->
          <div class="bb-field">
            <label class="bb-label" for="notification_body_ar">
              {{ 'Notification Content (Arabic)' | transloco }}
            </label>

            <textarea
              class="bb-input bb-textarea"
              id="notification_body_ar"
              rows="3"
              formControlName="notification_body_ar"
              placeholder="{{ 'Content in Arabic' | transloco }}"
              [ngClass]="{
                'is-invalid': notificationForm.get('notification_body_ar')?.touched && notificationForm.get('notification_body_ar')?.invalid,
                'is-valid': notificationForm.get('notification_body_ar')?.touched && notificationForm.get('notification_body_ar')?.valid
              }"
            ></textarea>
          </div>

          <!-- English Body -->
          <div class="bb-field">
            <label class="bb-label" for="notification_body_en">
              {{ 'Notification Content (English)' | transloco }}
            </label>

            <textarea
              class="bb-input bb-textarea"
              id="notification_body_en"
              rows="3"
              formControlName="notification_body_en"
              placeholder="{{ 'Content in English' | transloco }}"
              [ngClass]="{
                'is-invalid': notificationForm.get('notification_body_en')?.touched && notificationForm.get('notification_body_en')?.invalid,
                'is-valid': notificationForm.get('notification_body_en')?.touched && notificationForm.get('notification_body_en')?.valid
              }"
            ></textarea>
          </div>

          <!-- Desktop Image -->
          <div class="bb-field">
            <label class="bb-label">{{ 'Desktop Image' | transloco }}</label>

            <div class="bb-upload-card">
              <input type="file" #desktopFileInput hidden accept="image/*" (change)="onFileSelected($event)" />

              <div class="bb-upload-area" (click)="desktopFileInput.click()">
                <div *ngIf="!desktopPreview" class="bb-upload-placeholder">
                  <span class="bb-upload-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="7" width="18" height="14" rx="3" stroke="#2A7DE1" stroke-width="2.5" />
                      <circle cx="12" cy="14" r="3.5" stroke="#2A7DE1" stroke-width="2.5" />
                      <path d="M8 7L9.5 5H14.5L16 7" stroke="#2A7DE1" stroke-width="2.5" />
                    </svg>
                  </span>

                  <p class="bb-upload-title">{{ 'Upload desktop image' | transloco }}</p>
                  <small class="bb-upload-sub">{{ 'PNG / JPG' | transloco }}</small>
                </div>

                <div *ngIf="desktopPreview" class="bb-preview-container">
                  <img [src]="desktopPreview" class="bb-preview-img" />

                  <button
                    type="button"
                    class="bb-btn bb-btn-secondary bb-btn-sm"
                    (click)="desktopFileInput.click(); $event.stopPropagation()"
                  >
                    {{ 'Change image' | transloco }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Image -->
          <div class="bb-field">
            <label class="bb-label">{{ 'Mobile Image' | transloco }}</label>

            <div class="bb-upload-card">
              <input type="file" #mobileFileInput hidden accept="image/*" (change)="onMobileImageSelected($event)" />

              <div class="bb-upload-area" (click)="mobileFileInput.click()">
                <div *ngIf="!mobilePreview" class="bb-upload-placeholder">
                  <span class="bb-upload-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="7" width="18" height="14" rx="3" stroke="#2A7DE1" stroke-width="2.5" />
                      <circle cx="12" cy="14" r="3.5" stroke="#2A7DE1" stroke-width="2.5" />
                      <path d="M8 7L9.5 5H14.5L16 7" stroke="#2A7DE1" stroke-width="2.5" />
                    </svg>
                  </span>

                  <p class="bb-upload-title">{{ 'Upload mobile image' | transloco }}</p>
                  <small class="bb-upload-sub">{{ 'PNG / JPG' | transloco }}</small>
                </div>

                <div *ngIf="mobilePreview" class="bb-preview-container">
                  <img [src]="mobilePreview" class="bb-preview-img" />

                  <button
                    type="button"
                    class="bb-btn bb-btn-secondary bb-btn-sm"
                    (click)="mobileFileInput.click(); $event.stopPropagation()"
                  >
                    {{ 'Change image' | transloco }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Link -->
          <div class="bb-field">
            <label class="bb-label" for="link">{{ 'Notification Link' | transloco }}</label>

            <input
              class="bb-input"
              id="link"
              type="url"
              formControlName="link"
              placeholder="https://example.com"
              [ngClass]="{
                'is-invalid': notificationForm.get('link')?.touched && notificationForm.get('link')?.invalid,
                'is-valid': notificationForm.get('link')?.touched && notificationForm.get('link')?.valid
              }"
            />

            @if(shouldShowError('link')) {
              <div class="bb-field-error">
                {{ getErrorMessage('link', ('Notification Link' | transloco)) }}
              </div>
            }
          </div>

          <!-- Submit -->
          <div class="bb-field bb-flex bb-items-end bb-justify-end">
            <button
              class="bb-btn bb-btn-primary bb-btn-wide"
              type="submit"
              [class.is-disabled]="notificationForm.invalid"
            >
              {{ 'Submit' | transloco }}
            </button>
          </div>

        </div>
      </div>
    </form>

    <!-- Table Section -->
    <div class="bb-table-card bb-p-16 bb-mt-20">
      <label class="bb-label">{{ 'Notifications List' | transloco }}</label>

      <div class="bb-table-scroll">
        <table class="bb-table table">
          <thead>
            <tr>
              <th>{{ 'Title (AR)' | transloco }}</th>
              <th>{{ 'Title (EN)' | transloco }}</th>
              <th>{{ 'Content (AR)' | transloco }}</th>
              <th>{{ 'Content (EN)' | transloco }}</th>
              <th>{{ 'Link' | transloco }}</th>
              <th class="bb-actions-th">{{ 'Actions' | transloco }}</th>
            </tr>
          </thead>

          <tbody>
            <tr *ngFor="let element of dataSource.data">
              <td>{{ element.notification_head_ar }}</td>
              <td>{{ element.notification_head_en }}</td>
              <td>
                {{ element.notification_body_ar | slice:0:50 }}{{ element.notification_body_ar?.length > 50 ? '...' : '' }}
              </td>
              <td>
                {{ element.notification_body_en | slice:0:50 }}{{ element.notification_body_en?.length > 50 ? '...' : '' }}
              </td>
              <td>
                <a
                  *ngIf="element.link"
                  [href]="element.link"
                  target="_blank"
                  class="bb-link-action bb-truncate"
                >
                  {{ element.link }}
                </a>

                <span *ngIf="!element.link">-</span>
              </td>
              <td class="bb-actions-td">
                <button
                  type="button"
                  class="bb-btn bb-btn-danger bb-btn-sm"
                  (click)="deleteItem(element.id)"
                >
                  {{ 'Delete' | transloco }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Data State -->
      <div *ngIf="noData" class="bb-empty-state bb-text-center">
        <app-nodata></app-nodata>
      </div>
    </div>

  </div>
</section>